import React, { useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LaunchOfferModal = ({ isOpen, onClose, onSuccess }) => {
  const [email, setEmail] = useState('');
  const [country, setCountry] = useState('');
  const [claiming, setClaiming] = useState(false);

  if (!isOpen) return null;

  const handleClaim = async () => {
    if (!email || !country) {
      toast.error('Please fill in all fields');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setClaiming(true);
    try {
      const response = await axios.post(`${API}/offers/claim`, {
        email,
        country
      });

      if (response.data.success) {
        toast.success(response.data.message);
        localStorage.setItem('offerEmail', email);
        onSuccess();
        onClose();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Launch offer claim error:', error);
      toast.error(error.response?.data?.detail || 'Failed to claim launch offer');
    } finally {
      setClaiming(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px'
      }}
      onClick={handleOverlayClick}
    >
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '32px',
        maxWidth: '500px',
        width: '100%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        position: 'relative'
      }}>
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'transparent',
            border: 'none',
            fontSize: '28px',
            cursor: 'pointer',
            color: '#666',
            lineHeight: '1',
            padding: '4px 8px'
          }}
        >
          ×
        </button>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{ fontSize: '3rem', marginBottom: '12px' }}>🚀</div>
          <h2 style={{
            fontSize: '1.8rem',
            fontWeight: '700',
            color: '#1a202c',
            marginBottom: '8px'
          }}>
            Claim Your Launch Offer!
          </h2>
          <p style={{
            color: '#718096',
            fontSize: '1rem',
            lineHeight: '1.5'
          }}>
            Get <strong style={{ color: '#2563eb' }}>1 Year FREE</strong> Premium Access<br />
            Limited to first 50 users only!
          </p>
        </div>

        {/* Form */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            fontSize: '0.95rem',
            fontWeight: '600',
            color: '#2d3748',
            marginBottom: '8px'
          }}>
            Email Address
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your.email@example.com"
            style={{
              width: '100%',
              padding: '12px 16px',
              fontSize: '1rem',
              border: '2px solid #e2e8f0',
              borderRadius: '8px',
              outline: 'none',
              transition: 'border-color 0.3s'
            }}
            onFocus={(e) => e.target.style.borderColor = '#2563eb'}
            onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
          />
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label style={{
            display: 'block',
            fontSize: '0.95rem',
            fontWeight: '600',
            color: '#2d3748',
            marginBottom: '8px'
          }}>
            Country
          </label>
          <input
            type="text"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            placeholder="United States"
            style={{
              width: '100%',
              padding: '12px 16px',
              fontSize: '1rem',
              border: '2px solid #e2e8f0',
              borderRadius: '8px',
              outline: 'none',
              transition: 'border-color 0.3s'
            }}
            onFocus={(e) => e.target.style.borderColor = '#2563eb'}
            onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
          />
        </div>

        {/* Claim Button */}
        <button
          onClick={handleClaim}
          disabled={claiming}
          style={{
            width: '100%',
            background: claiming ? '#cbd5e0' : 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
            color: 'white',
            border: 'none',
            padding: '14px 32px',
            borderRadius: '8px',
            fontSize: '1.1rem',
            fontWeight: '700',
            cursor: claiming ? 'not-allowed' : 'pointer',
            transition: 'all 0.3s',
            boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)'
          }}
          onMouseOver={(e) => {
            if (!claiming) {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 16px rgba(37, 99, 235, 0.4)';
            }
          }}
          onMouseOut={(e) => {
            if (!claiming) {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.3)';
            }
          }}
        >
          {claiming ? '⏳ Claiming...' : '🚀 Claim My Free Year!'}
        </button>

        {/* Info Box */}
        <div style={{
          marginTop: '20px',
          padding: '16px',
          background: 'linear-gradient(135deg, #ebf8ff 0%, #e0f2fe 100%)',
          border: '2px solid #3b82f6',
          borderRadius: '8px'
        }}>
          <p style={{
            color: '#1e40af',
            fontSize: '0.875rem',
            margin: 0,
            lineHeight: '1.5'
          }}>
            <strong>✨ What you get:</strong><br />
            • 1 Year of Premium Access (FREE)<br />
            • Unlimited PDF processing<br />
            • All premium features unlocked<br />
            • Priority support
          </p>
        </div>
      </div>
    </div>
  );
};

export default LaunchOfferModal;
